import { useState, useEffect } from 'react';
import { messagingApi } from '../../services/messaging-api';
import { customerTypesService, CustomerType } from '../../services/customerTypes.service';
import { showToast } from '../../utils/toast';

type MessageComposerProps = {
  tenantId?: string;
  onNavigate?: (page: string) => void;
};

type MessageMode = 'quick' | 'batch';

export function MessageComposer({
  tenantId = '',
  onNavigate,
}: MessageComposerProps) {
  const [mode, setMode] = useState<MessageMode>('quick');
  const [providers, setProviders] = useState<any[]>([]);
  const [templates, setTemplates] = useState<any[]>([]);
  const [customerTypes, setCustomerTypes] = useState<CustomerType[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [recipients, setRecipients] = useState<any[]>([]);
  const [showRecipients, setShowRecipients] = useState(false);
  const [loadingRecipients, setLoadingRecipients] = useState(false);
  const [editBatchId, setEditBatchId] = useState<string>('');
  const [isLoadingBatchData, setIsLoadingBatchData] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  const normalizeRecipient = (recipient: any) => ({
    ...recipient,
    customerName:
      recipient.customerName ||
      recipient.customer?.firstName ||
      recipient.firstName ||
      recipient.customer?.name ||
      'Unknown',
    phone: recipient.phone || recipient.customer?.phone,
    email: recipient.email || recipient.customer?.email,
  });

  const [formData, setFormData] = useState({
    // Message content
    providerId: '',
    templateId: '',
    channel: 'SMS' as 'SMS' | 'EMAIL',
    subject: '',
    message: '',

    // Recipients
    recipientMode: 'single' as 'single' | 'batch',
    singleRecipient: '',
    customerStatus: 'all' as 'all' | 'active' | 'inactive',
    customerTypeId: 'all' as string,

    // Send options
    sendMode: 'immediate' as 'immediate' | 'queued',
  });

  useEffect(() => {
    loadData();
    const storedBatchId = sessionStorage.getItem('editBatchId');
    if (storedBatchId) {
      sessionStorage.removeItem('editBatchId');
      setEditBatchId(storedBatchId);
      setIsEditMode(true);
      loadBatchForEdit(storedBatchId);
    }
  }, [tenantId]);

  const loadData = async () => {
    try {
      const [providersRes, templatesRes, customerTypesRes] = await Promise.all([
        messagingApi.getProviders(tenantId),
        messagingApi.getTemplates(tenantId),
        customerTypesService.getAll(tenantId),
      ]);
      setProviders(providersRes.data);
      setTemplates(templatesRes.data);
      setCustomerTypes(customerTypesRes.data || []);
    } catch (err) {
      console.error('Failed to load data', err);
      showToast.error('Failed to load messaging data');
    } finally {
      setLoading(false);
    }
  };

  const loadBatchForEdit = async (batchIdToEdit: string) => {
    if (!batchIdToEdit) return;
    setIsLoadingBatchData(true);
    try {
      const response = await messagingApi.getBatch(batchIdToEdit);
      const batch = response.data;
      if (!batch) {
        showToast.error('Batch not found for editing');
        return;
      }

      setEditBatchId(batchIdToEdit);
      setIsEditMode(true);

      setFormData({
        providerId: batch.providerId || '',
        templateId: batch.templateId || '',
        channel: batch.channel || 'SMS',
        subject: batch.subject || '',
        message: batch.message || '',
        recipientMode: 'batch',
        singleRecipient: '',
        customerStatus: batch.customerStatus || 'all',
        customerTypeId:
          batch.customerTypeIds?.length === 1
            ? batch.customerTypeIds[0]
            : batch.customerTypeIds?.length > 1
            ? batch.customerTypeIds[0]
            : 'all',
        sendMode: batch.sendMode || 'immediate',
      });

      const loadedRecipients = (batch.recipients || []).map(normalizeRecipient);
      setRecipients(loadedRecipients);
      setShowRecipients(loadedRecipients.length > 0);
    } catch (err: any) {
      console.error('Failed to load batch for edit:', err);
      showToast.error('Failed to load batch for editing');
    } finally {
      setIsLoadingBatchData(false);
    }
  };

  const handleChange = (e: any) => {
    const { name, value } = e.target;

    if (name === 'templateId') {
      const selectedTemplate = templates.find((t) => t.id === value);
      setFormData((prev) => ({
        ...prev,
        templateId: value,
        channel: selectedTemplate?.channel || prev.channel,
        message: selectedTemplate?.body || prev.message,
        subject: selectedTemplate?.subject || prev.subject,
      }));
      return;
    }

    if (name === 'channel') {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        templateId: '',
        message: '',
        subject: '',
      }));
      return;
    }

    if (name === 'recipientMode') {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        singleRecipient: value === 'single' ? prev.singleRecipient : '',
        customerStatus: value === 'batch' ? prev.customerStatus : 'all',
        customerTypeId: value === 'batch' ? prev.customerTypeId : 'all',
      }));
      setShowRecipients(false);
      setRecipients([]);
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleLoadRecipients = async () => {
    if (formData.recipientMode !== 'batch') return;

    setLoadingRecipients(true);
    try {
      const response = await messagingApi.previewRecipients({
        tenantId,
        channel: formData.channel,
        customerTypeIds: formData.customerTypeId === 'all' ? undefined : [formData.customerTypeId],
        customerStatus: formData.customerStatus === 'all' ? undefined : formData.customerStatus,
      });

      const loadedRecipients = response.data || [];
      setRecipients(loadedRecipients);
      setShowRecipients(true);

      if (loadedRecipients.length === 0) {
        showToast.warning('No recipients found matching the selected filters');
      } else {
        showToast.success(`Found ${loadedRecipients.length} recipient(s)`);
      }
    } catch (err: any) {
      showToast.error('Failed to load recipients: ' + (err.message || 'Unknown error'));
      console.error(err);
    } finally {
      setLoadingRecipients(false);
    }
  };

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);

    try {
      if (formData.recipientMode === 'single') {
        // Send single message
        if (!formData.singleRecipient.trim()) {
          showToast.error('Please enter a recipient');
          return;
        }

        // For now, create a batch with single recipient
        const batchData = {
          tenantId,
          providerId: formData.providerId,
          templateId: formData.templateId,
          channel: formData.channel,
          name: `Quick Message - ${Date.now()}`,
          subject: formData.subject,
          message: formData.message,
          selectedCustomerIds: [], // Would need to resolve recipient to customer ID
          customerTypeIds: [],
          customerStatus: undefined,
        };

        if (isEditMode && editBatchId) {
          await messagingApi.updateBatch(editBatchId, batchData);
          showToast.success('Message updated successfully!');
        } else {
          await messagingApi.createBatch(batchData);
          showToast.success('Message sent successfully!');
        }
      } else {
        // Send batch message
        if (recipients.length === 0) {
          showToast.error('Please load recipients first');
          return;
        }

        const batchData = {
          tenantId,
          providerId: formData.providerId,
          templateId: formData.templateId,
          channel: formData.channel,
          name: isEditMode ? `Updated Batch - ${Date.now()}` : `Batch Message - ${Date.now()}`,
          subject: formData.subject,
          message: formData.message,
          customerStatus: formData.customerStatus === 'all' ? undefined : formData.customerStatus,
          customerTypeIds: formData.customerTypeId === 'all' ? [] : [formData.customerTypeId],
        };

        if (isEditMode && editBatchId) {
          await messagingApi.updateBatch(editBatchId, batchData);
          if (recipients.length > 0) {
            await messagingApi.loadRecipients(editBatchId, {
              customerTypeIds: formData.customerTypeId === 'all' ? undefined : [formData.customerTypeId],
              customerStatus: formData.customerStatus === 'all' ? undefined : formData.customerStatus,
            });
          }
          showToast.success(`Batch updated with ${recipients.length} recipients!`);
        } else {
          const response = await messagingApi.createBatch(batchData);
          const batchId = response.data?.id;

          if (batchId) {
            await messagingApi.loadRecipients(batchId, {
              customerTypeIds: formData.customerTypeId === 'all' ? undefined : [formData.customerTypeId],
              customerStatus: formData.customerStatus === 'all' ? undefined : formData.customerStatus,
            });
          }
          showToast.success(`Batch message created with ${recipients.length} recipients!`);
        }
      }

      // Reset form
      setFormData({
        providerId: '',
        templateId: '',
        channel: 'SMS',
        subject: '',
        message: '',
        recipientMode: 'single',
        singleRecipient: '',
        customerStatus: 'all',
        customerTypeId: 'all',
        sendMode: 'immediate',
      });
      setRecipients([]);
      setShowRecipients(false);
      setEditBatchId('');
      setIsEditMode(false);
    } catch (err: any) {
      showToast.error('Failed to send message: ' + (err.message || 'Unknown error'));
      console.error(err);
    } finally {
      setSending(false);
    }
  };

  if (loading || isLoadingBatchData) return <div className="p-6">Loading...</div>;

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-8">
        <button
          onClick={() => onNavigate?.('message-batches')}
          className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 text-sm font-medium mb-4"
        >
          ← Back to Messages
        </button>
        <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">{isEditMode ? 'Edit Message Batch' : 'Send Message'}</h1>
        <p className="text-gray-600 dark:text-gray-400">{isEditMode ? 'Update batch details and recipients.' : 'Send a quick message or create a batch message to multiple recipients.'}</p>
      </div>

      <form onSubmit={handleSend} className="space-y-6">
        {/* Message Type Selection */}
        <div className="rounded-3xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-900">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Message Type</h2>
          <div className="flex gap-4">
            <label className="flex items-center">
              <input
                type="radio"
                name="recipientMode"
                value="single"
                checked={formData.recipientMode === 'single'}
                onChange={handleChange}
                className="mr-2"
              />
              <span className="font-medium">Quick Message</span>
              <span className="text-sm text-gray-500 ml-2">Send to one recipient</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="recipientMode"
                value="batch"
                checked={formData.recipientMode === 'batch'}
                onChange={handleChange}
                className="mr-2"
              />
              <span className="font-medium">Batch Message</span>
              <span className="text-sm text-gray-500 ml-2">Send to multiple recipients</span>
            </label>
          </div>
        </div>

        {/* Recipients Section */}
        <div className="rounded-3xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-900">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Recipients</h2>

          {formData.recipientMode === 'single' ? (
            <div>
              <label className="block font-semibold mb-2 text-gray-900 dark:text-white">Recipient</label>
              <input
                type="text"
                name="singleRecipient"
                value={formData.singleRecipient}
                onChange={handleChange}
                placeholder="Enter phone number or email"
                className="border rounded-lg bg-white text-gray-900 px-3 py-2 w-full border-gray-300 focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white dark:border-gray-700"
                required
              />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block font-semibold mb-2 text-gray-900 dark:text-white">Customer Status</label>
                  <select
                    name="customerStatus"
                    value={formData.customerStatus}
                    onChange={handleChange}
                    className="border rounded-lg bg-white text-gray-900 px-3 py-2 w-full border-gray-300 focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white dark:border-gray-700"
                  >
                    <option value="all">All customers</option>
                    <option value="active">Active customers</option>
                    <option value="inactive">Inactive customers</option>
                  </select>
                </div>

                <div>
                  <label className="block font-semibold mb-2 text-gray-900 dark:text-white">Customer Type</label>
                  <select
                    name="customerTypeId"
                    value={formData.customerTypeId}
                    onChange={handleChange}
                    className="border rounded-lg bg-white text-gray-900 px-3 py-2 w-full border-gray-300 focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white dark:border-gray-700"
                  >
                    <option value="all">All types</option>
                    {customerTypes.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <button
                type="button"
                onClick={handleLoadRecipients}
                disabled={loadingRecipients}
                className="w-full bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 disabled:bg-gray-400 font-medium"
              >
                {loadingRecipients ? 'Loading Recipients...' : 'Preview Recipients'}
              </button>

              {showRecipients && (
                <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white">
                        Recipients Found: {recipients.length}
                      </h3>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                        Filters: {formData.customerStatus} • Types: {formData.customerTypeId === 'all' ? 'All types' : customerTypes.find(t => t.id === formData.customerTypeId)?.name || 'selected'}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowRecipients(false)}
                      className="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 font-medium"
                    >
                      Hide
                    </button>
                  </div>
                  {recipients.length > 0 ? (
                    <div className="max-h-56 overflow-y-auto space-y-1">
                      {recipients.slice(0, 10).map((r, i) => (
                        <div key={i} className="text-sm text-gray-700 dark:text-gray-300 px-2 py-1 bg-white dark:bg-gray-800 rounded border">
                          <div className="font-medium">{r.customerName || 'Unknown'}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {r.phone && `📱 ${r.phone}`} {r.email && `✉️ ${r.email}`}
                          </div>
                        </div>
                      ))}
                      {recipients.length > 10 && (
                        <div className="text-sm text-gray-500 dark:text-gray-400 pt-2 px-2 border-t border-blue-200 dark:border-blue-800 font-medium">
                          ...and {recipients.length - 10} more recipients
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-600 dark:text-gray-400 py-4 text-center">
                      No recipients found with selected filters.
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Message Content Section */}
        <div className="rounded-3xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-900">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Message Content</h2>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block font-semibold mb-2 text-gray-900 dark:text-white">Channel *</label>
                <select
                  name="channel"
                  value={formData.channel}
                  onChange={handleChange}
                  className="border rounded-lg bg-white text-gray-900 px-3 py-2 w-full border-gray-300 focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white dark:border-gray-700"
                >
                  <option value="SMS">SMS</option>
                  <option value="EMAIL">Email</option>
                </select>
              </div>

              <div>
                <label className="block font-semibold mb-2 text-gray-900 dark:text-white">Provider *</label>
                <select
                  name="providerId"
                  value={formData.providerId}
                  onChange={handleChange}
                  required
                  className="border rounded-lg bg-white text-gray-900 px-3 py-2 w-full border-gray-300 focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white dark:border-gray-700"
                >
                  <option value="">Select a provider</option>
                  {providers.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block font-semibold mb-2 text-gray-900 dark:text-white">Template (Optional)</label>
              <select
                name="templateId"
                value={formData.templateId}
                onChange={handleChange}
                className="border rounded-lg bg-white text-gray-900 px-3 py-2 w-full border-gray-300 focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white dark:border-gray-700"
              >
                <option value="">Choose a template or write custom</option>
                {templates
                  .filter((t) => t.channel === formData.channel)
                  .map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
              </select>
            </div>

            {formData.channel === 'EMAIL' && (
              <div>
                <label className="block font-semibold mb-2 text-gray-900 dark:text-white">Subject</label>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  placeholder="Email subject"
                  className="border rounded-lg bg-white text-gray-900 px-3 py-2 w-full border-gray-300 focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white dark:border-gray-700"
                />
              </div>
            )}

            <div>
              <label className="block font-semibold mb-2 text-gray-900 dark:text-white">Message *</label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                rows={5}
                placeholder="Type your message here..."
                className="border rounded-lg bg-white text-gray-900 px-3 py-2 w-full border-gray-300 focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white dark:border-gray-700"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                💡 Tip: Use {`{{firstName}}`}, {`{{phone}}`}, {`{{email}}`} for personalization
              </p>
            </div>
          </div>
        </div>

        {/* Send Options */}
        <div className="rounded-3xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-900">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Send Options</h2>
          <div className="flex gap-4">
            <label className="flex items-center">
              <input
                type="radio"
                name="sendMode"
                value="immediate"
                checked={formData.sendMode === 'immediate'}
                onChange={handleChange}
                className="mr-2"
              />
              <span className="font-medium">Send Immediately</span>
              <span className="text-sm text-gray-500 ml-2">Send message right now</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="sendMode"
                value="queued"
                checked={formData.sendMode === 'queued'}
                onChange={handleChange}
                className="mr-2"
              />
              <span className="font-medium">Add to Queue</span>
              <span className="text-sm text-gray-500 ml-2">Send in FIFO order</span>
            </label>
          </div>
        </div>

        <div className="flex gap-3 pt-4">
          <button
            type="submit"
            disabled={sending || (formData.recipientMode === 'batch' && !showRecipients)}
            className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 font-medium"
            title={formData.recipientMode === 'batch' && !showRecipients ? 'Preview recipients first' : ''}
          >
            {sending ? (isEditMode ? 'Updating...' : 'Sending...') : (isEditMode ? `Update ${formData.recipientMode === 'single' ? 'Message' : 'Batch'}` : `Send ${formData.recipientMode === 'single' ? 'Message' : 'Batch'}`)}
          </button>

          <button
            type="button"
            onClick={() => onNavigate?.('message-batches')}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 font-medium"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}